import { useEffect, useMemo, useState } from 'react';
import type { FormControl } from 'features/offline-reports/support-requests/components/support-requests-modals/types';
import {
    FormSectionType,
    FormsSectionManager,
    PropertyFormValues,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { useRequestPropertyFormSchema } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/use-form-config';
import { ModalManager } from '@placer-ui/components';
import { useSupportModalProps } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/use-support-modal-props';

const modalManager = ModalManager.getInstance();

export const useFormsSectionManager = (
    formController: FormControl<PropertyFormValues>,
): FormsSectionManager<FormSectionType> => {
    const [activeSectionIndex, setActiveSectionIndex] = useState(0);

    const inputTypeField = formController.watch('inputType');
    const { getSectionConfig } = useSupportModalProps();
    const { formsConfig } = useRequestPropertyFormSchema(inputTypeField, setActiveSectionIndex);

    useEffect(() => {
        const activeSectionType = formsConfig[activeSectionIndex]?.type;

        modalManager.updateModal('property', getSectionConfig(activeSectionType));
    }, [activeSectionIndex, formsConfig, getSectionConfig]);

    return useMemo(() => {
        const activeSection = formsConfig[activeSectionIndex];

        const handleNextSection = async () => {
            const nextSection = formsConfig[activeSectionIndex + 1];
            if (nextSection) {
                setActiveSectionIndex((prev) => prev + 1);
            }
        };

        const formLength = formsConfig?.length - 1;

        return {
            formConfig: formsConfig,
            activeSectionIndex,
            setActiveSectionIndex,
            nextSection: handleNextSection,
            previousSection: () => setActiveSectionIndex((prev) => Math.max(prev - 1, 0)),
            activeSectionType: activeSection?.type,
            formLength,
            sectionConfig: activeSection,
            FormSection: activeSection?.Form ?? null,
            isLastStep: activeSectionIndex === formLength,
        };
    }, [activeSectionIndex, formsConfig]);
};
